import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { CurrentUser, OgData, OnboardingPageContent } from 'types';
import { SIGN_UP_STEP_CAPTIONS } from 'consts';
import { checkIfFullyAuthorized, getAfterLogInRedirectPath, getSignUpSteps } from 'utils/services';
import { trackSessionRedirects } from 'utils/localStoreTools';
import { HeadComponent } from 'components/HeadComponent';
import { SignUpTitlePage } from './SignUpTitlePage';
import { SignUpStepper } from './SignUpStepper';
import { SeoHiddenContent } from 'components/SeoHiddenContent';
import { getPageH1Title } from 'utils/seoFormatters';
import { setUser } from '../../utils/analytics';

export type SignUpProps = {
  email: string;
  activities?: Record<string, string>;
  brands?: Record<string, string>;
  query?: ParsedUrlQuery;
  ogData?: OgData[];
  onboarding: OnboardingPageContent;
  user?: CurrentUser;
};

export const SignUp = (props: SignUpProps) => {
  const router = useRouter();
  const [exitStepper, setExitStepper] = useState(false);
  const { onboarding, user, query, email, externalReviews, prPosts, faqTopics } = props;
  const [currentUser, setCurrentUser] = useState(user);
  const isFullyAuthorized = checkIfFullyAuthorized(currentUser);

  useLayoutEffect(() => {
    if (isFullyAuthorized) {
      if(currentUser?.id)
        setUser(currentUser.id)
      trackSessionRedirects(router, () => {
        router.push(getAfterLogInRedirectPath(query)).then(() => {});
      });
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentUser) return;
    const signUpSteps = getSignUpSteps(currentUser);
    if (signUpSteps?.passed.length < SIGN_UP_STEP_CAPTIONS.length && !currentUser.signUpSteps) {
      setCurrentUser({ ...currentUser, signUpSteps });
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const seoHiddenComponent = <SeoHiddenContent title={getPageH1Title('signUp')} />;

  if (currentUser && currentUser.tac_accepted_at && !exitStepper) {
    return (
      <>
        <HeadComponent headTitle="Sign Up | SENDY" ogData={props.ogData} />
        {seoHiddenComponent}
        <SignUpStepper
          {...props}
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
          exitStepper={() => setExitStepper(true)}
        />
      </>
    );
  }

  return (
    <>
      <HeadComponent headTitle="Sign Up | SENDY" ogData={props.ogData} />
      {seoHiddenComponent}
      <SignUpTitlePage
        currentUser={currentUser}
        enterStepper={() => setExitStepper(false)}
        query={query}
        email={email}
        signInSlides={onboarding?.sections || []}
        setCurrentUser={setCurrentUser}
        externalReviews={externalReviews}
        prPosts={prPosts}
        faqTopics={faqTopics}
      />
    </>
  );
};
